// ui/useBotScheduler.js: M2.c-10c bot turn scheduler.
//
// Watches the reducer snapshot. When current_turn_seat != humanSeat and
// the game wants an action from a bot, schedules setTimeout(thinkMs)
// then runs the bot's choice through the existing useGameLoop dispatch
// surface (submitBid / dealerDiscard / playCard).
//
// Action-id cancellation: each scheduled fire carries an action_id
// derived from the snapshot. If a state change moves the action_id
// before the timer fires, the late callback no-ops. Belt-and-suspenders:
// we also clearTimeout on snapshot change.
//
// Why both action_id AND clearTimeout: clearTimeout is the cheap path,
// but Preact's effect cleanup runs *after* the next effect schedules,
// which means a fast double-tick could in theory queue a second timer
// before the cleanup fires. The action_id guard at fire time is the
// authoritative check.

import { useEffect, useRef } from 'preact/hooks';
import { ACTIONS } from './gameReducer.js';

// Reveal beat: after the bot's annotation fires, hold the action for
// this many ms so the player can read the annotation before the
// snapshot replaces. Calibrated against reading-comprehension lower
// bound for short callouts (Hearthstone/MTGA pacing benchmark).
const REVEAL_BEAT_MS = 250;
export const _internalsForTests = { REVEAL_BEAT_MS };

/**
 * Build a stable "what turn is this?" key from the snapshot. Same key
 * across re-renders without a real state change ⇒ the scheduled bot
 * action is still relevant. Different key ⇒ stale ⇒ no-op.
 *
 * Includes bid_history.length + trick_plays.length so that two
 * consecutive bot passes in round-2 bidding (same current_state +
 * same current_turn_seat after seat wrap-around) read as DIFFERENT
 * action_ids, otherwise the cancellation guard would suppress the
 * second bot bid.
 *
 * @param {object|null} snap
 * @returns {string|null} null when snapshot can't drive a turn yet.
 */
export function deriveActionId(snap) {
  if (!snap) return null;
  const histLen   = Array.isArray(snap.bid_history)  ? snap.bid_history.length  : 0;
  const playsLen  = Array.isArray(snap.trick_plays)  ? snap.trick_plays.length  : 0;
  const handIx    = snap.hand_index ?? 0;
  const trickNo   = snap.trick_number ?? 0;
  const state     = snap.current_state ?? 'unknown';
  const turn      = snap.current_turn_seat ?? 0;
  return `${handIx}|${state}|${turn}|${trickNo}|${histLen}|${playsLen}`;
}

/**
 * True when the snapshot is in a state that expects a bot action from
 * `current_turn_seat` (and that seat is not the human).
 *
 * @param {object|null} snap
 * @param {1|2|3|4} humanSeat
 */
export function shouldBotAct(snap, humanSeat) {
  if (!snap) return false;
  const turn = snap.current_turn_seat;
  if (turn == null || turn === humanSeat) return false;
  // Sitting-out seat must never be asked to act.
  if (snap.sitting_out_seat != null && turn === snap.sitting_out_seat) return false;
  switch (snap.current_state) {
    case 'bid_r1':
    case 'bid_r2':
    case 'trick_play':
    case 'dealer_discard':
      return true;
    default:
      return false;
  }
}

/**
 * Translate a bot decision + snapshot into a reducer command action.
 * Returns null if the decision is empty / state isn't actionable.
 *
 * @param {object} snap
 * @param {{action?: string, suit?: string, card_id?: string}} decision
 * @param {typeof import('./gameReducer.js').actionCreators} actionCreators
 */
export function botDecisionToAction(snap, decision, actionCreators) {
  if (!decision) return null;
  const seat = snap.current_turn_seat;
  switch (snap.current_state) {
    case 'bid_r1':
    case 'bid_r2': {
      if (!decision.action) return null;
      return actionCreators.submitBid(seat, {
        action: decision.action,
        suit:   decision.suit,
      });
    }
    case 'trick_play': {
      if (!decision.card_id) return null;
      return actionCreators.playCard(seat, decision.card_id);
    }
    case 'dealer_discard': {
      if (!decision.card_id) return null;
      return actionCreators.dealerDiscard(decision.card_id);
    }
    default:
      return null;
  }
}

/**
 * Schedules bot turns when current_turn_seat is a bot. Drives commands
 * through the supplied `dispatch` (the same dispatch returned by
 * useGameLoop). The hook is a no-op when luaGameState is null.
 *
 * @param {{
 *   snapshot: object|null,
 *   dispatch: (a: any) => void,
 *   actionCreators: typeof import('./gameReducer.js').actionCreators,
 *   luaGameState: import('../lua/game/lua_bridge.js').LuaGameState|null,
 *   humanSeat: 1|2|3|4,
 *   reducedMotion?: boolean,
 *   onCancel?: (actionId: string) => void,
 *   onBotAnnotation?: (seat: number, text: string) => void,
 * }} opts
 */
export function useBotScheduler(opts) {
  const {
    snapshot, dispatch, actionCreators, luaGameState,
    humanSeat, reducedMotion = false, onCancel, onBotAnnotation,
  } = opts;

  // Holds the "current" action_id, every scheduled callback checks
  // this at fire time. Live in a ref so updates don't trigger
  // re-renders.
  const currentActionIdRef = useRef(/** @type {string|null} */ (null));
  const timerRef           = useRef(/** @type {ReturnType<typeof setTimeout>|null} */ (null));

  useEffect(() => {
    const newId = deriveActionId(snapshot);
    currentActionIdRef.current = newId;

    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!luaGameState || !newId) return;
    if (!shouldBotAct(snapshot, humanSeat)) return;

    const seat = snapshot.current_turn_seat;
    // Reduced-motion: skip the think delay. Otherwise honor the bot's
    // own stochastic delay (200..600ms).
    let delay = 0;
    if (!reducedMotion) {
      try { delay = luaGameState.botThinkDelayMs(seat); }
      catch { delay = 350; }
    }

    const scheduledId = newId;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (currentActionIdRef.current !== scheduledId) {
        onCancel?.(scheduledId);
        return;
      }
      try {
        let decision;
        if (snapshot.current_state === 'dealer_discard') {
          decision = luaGameState.botDealerDiscard(seat);
        } else if (snapshot.current_state === 'trick_play') {
          decision = luaGameState.botPlay(seat);
        } else {
          decision = luaGameState.botBid(seat);
        }
        const annotation = /** @type {any} */ (decision)?.annotation;
        if (annotation && onBotAnnotation) {
          onBotAnnotation(seat, annotation);
        }
        const action = botDecisionToAction(snapshot, decision, actionCreators);
        if (!action) return;

        // Reveal beat: when we have an annotation AND motion is not
        // reduced, hold the action for REVEAL_BEAT_MS so the player
        // has time to read the bot's reasoning before the snapshot
        // replaces. action_id cancellation still applies, a snapshot
        // change during the beat invalidates the dispatch.
        if (annotation && !reducedMotion) {
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (currentActionIdRef.current !== scheduledId) {
              onCancel?.(scheduledId);
              return;
            }
            try {
              dispatch(action);
            } catch (err) {
              dispatch(actionCreators.commandFailed(errMsg(err)));
            }
          }, REVEAL_BEAT_MS);
        } else {
          dispatch(action);
        }
      } catch (err) {
        // Surface as a COMMAND_FAILED so the user can see the bot
        // crashed; useGameLoop's reducer treats commandFailed as a
        // recoverable surface (state preserved).
        dispatch(actionCreators.commandFailed(errMsg(err)));
      }
    }, delay);

    return () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [snapshot, luaGameState, humanSeat, reducedMotion, dispatch, actionCreators, onCancel, onBotAnnotation]);
}

function errMsg(err) {
  if (err instanceof Error) return err.message;
  return String(err);
}
